import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api } from "../lib/api";

const POLL_MS = 5_000;

/**
 * useAnnotationVersion — polls /v2/docs/:id/annotation-version every 5 s.
 * When the version changes vs the previously seen one, the local
 * `doc-annotations` query is invalidated so the viewer re-fetches the
 * full annotation set. Polling only runs when `enabled` is true (typically
 * gated on presence>1 to avoid burning D1 when the user is alone).
 */
export function useAnnotationVersion(
  docId: string | undefined,
  enabled: boolean,
): { version: number | null } {
  const qc = useQueryClient();
  const lastSeen = useRef<number | null>(null);

  const q = useQuery({
    queryKey: ["doc-annotation-version", docId],
    queryFn: () => api.getAnnotationVersion(docId ?? ""),
    enabled: !!docId && enabled,
    refetchInterval: POLL_MS,
    staleTime: 0,
  });

  useEffect(() => {
    if (!q.data) return;
    if (lastSeen.current == null) {
      lastSeen.current = q.data.version;
      return;
    }
    if (q.data.version !== lastSeen.current) {
      lastSeen.current = q.data.version;
      void qc.invalidateQueries({ queryKey: ["doc-annotations", docId] });
    }
  }, [q.data, docId, qc]);

  return { version: q.data?.version ?? null };
}
